// PiezaPreviewIG — mockup de celular + modal "todo con detalle" para piezas de posts_ig.
// Pedido de Nico (26-jul-2026): "me gustaría tener cuando veo que pueda ver como si
// fuera un celular. foto, texto y #" + "y quiero ver todo con detalle".
// El chassis del celular está extraído del PreviewPanel de MarketingHub (mh2-phone),
// acá vive como componente compartido de la sección COMUNICACIÓN para que el
// Agente de Redes (y a futuro el taller de /admin/marketing) muestren la pieza
// exactamente como se va a ver en Instagram.

import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'

// ── Parsers robustos (Supabase a veces entrega jsonb como string) ──
export function parseJsonbArray(val) {
  if (!val) return []
  if (Array.isArray(val)) return val
  if (typeof val === 'string') {
    try {
      const p = JSON.parse(val)
      return Array.isArray(p) ? p : []
    } catch { return [] }
  }
  if (typeof val === 'object') return Object.values(val)
  return []
}

export function parseJsonbObject(val) {
  if (!val) return null
  if (typeof val === 'object' && !Array.isArray(val)) return val
  if (typeof val === 'string') {
    try { return JSON.parse(val) } catch { return null }
  }
  return null
}

// Formatos verticales 9:16 (story/reel); el resto va 4:5 como el feed.
const FORMATOS_STORY = new Set(['story', 'reel', 'short', 'status', 'video'])

const PILAR_LABEL = {
  costo_invisible: 'Costo invisible',
  diccionario: 'Diccionario',
  diario_barrio: 'Diario de barrio',
  expectativa: 'Expectativa',
  lanzamiento: 'Lanzamiento',
  retencion: 'Retención',
  otro: 'Otro',
}

const ESTADO_PILL = {
  borrador: { label: 'Borrador', cls: 'pig-pill-gold' },
  generado: { label: 'Espera tu OK', cls: 'pig-pill-teal' },
  aprobado: { label: 'Aprobada', cls: 'pig-pill-green' },
  programado: { label: 'Programada', cls: 'pig-pill-green' },
  publicando: { label: 'Publicando…', cls: 'pig-pill-teal' },
  publicado: { label: 'Publicada', cls: 'pig-pill-green' },
  rechazado: { label: 'Rechazada', cls: 'pig-pill-red' },
}

function stripNegritas(s) {
  return (s || '').toString().replace(/\*\*([^*]+)\*\*/g, '$1')
}

function normalizarHashtags(pieza) {
  return parseJsonbArray(pieza?.hashtags)
    .map(h => (h || '').toString().trim())
    .filter(Boolean)
    .map(h => (h.startsWith('#') ? h : '#' + h))
}

// ── Validador de hechos (gerente v27) ──
// El validador determinista deja entradas con prefijo en feedback_gerente:
//   VALIDADOR_OK: / VALIDADOR_ADVERTENCIA: / VALIDADOR_ERROR:
// Acá se separan del feedback humano para el bloque "Datos verificados".
export function validadorDePieza(pieza) {
  const arr = parseJsonbArray(pieza?.feedback_gerente)
    .map(f => (typeof f === 'string' ? f : JSON.stringify(f)))
  const advertencias = arr.filter(f => f.startsWith('VALIDADOR_ADVERTENCIA:')).map(f => f.slice(22).trim())
  const errores = arr.filter(f => f.startsWith('VALIDADOR_ERROR:')).map(f => f.slice(16).trim())
  const ok = arr.some(f => f.startsWith('VALIDADOR_OK:'))
  const resto = arr.filter(f => !f.startsWith('VALIDADOR_'))
  return { verificada: ok && errores.length === 0, advertencias, errores, resto }
}

// Estado de la foto de escena IA (generar-escena-post escribe imagenes_renders.escena).
export function escenaDePieza(pieza) {
  const rend = parseJsonbObject(pieza?.imagenes_renders)
  const esc = rend?.escena
  return esc && typeof esc === 'object' ? esc : null
}

// ── Imágenes de la pieza, ordenadas por slide ──
// Junta todo lo que haya: imagenes_renders por índice ({"0":{"url":…}}),
// renders por motor ({ideogram:[…], flux:[…]}) e imagenes_urls (text[]).
export function imagenesDePieza(pieza) {
  const map = new Map()
  const rend = parseJsonbObject(pieza?.imagenes_renders)
  if (rend) {
    // Formato nuevo: {"0": {"url": "…"}, "1": "…"}
    Object.entries(rend).forEach(([k, v]) => {
      const i = Number(k)
      if (Number.isNaN(i)) return
      const u = typeof v === 'string' ? v : (v && typeof v === 'object' ? v.url : null)
      if (u) map.set(i, u)
    })
    // Formato viejo por motor: {ideogram: […]} / {flux: […]}
    const motor = parseJsonbArray(rend.ideogram).length
      ? parseJsonbArray(rend.ideogram)
      : parseJsonbArray(rend.flux)
    motor.forEach((u, i) => { if (u && !map.has(i)) map.set(i, u) })
    // Foto de escena IA (nano-banana) y placa CSS como fallback del slide 0.
    // Viven en imagenes_renders.escena.url / .css[] — claves NO numéricas que el
    // loop de arriba saltea. Sin esto la miniatura de la cola queda en 🖼️ aunque
    // la foto ya esté lista (solo se veía dentro del modal celular).
    const escenaUrl = rend.escena && typeof rend.escena === 'object' ? rend.escena.url : null
    if (escenaUrl && !map.has(0)) map.set(0, escenaUrl)
    const cssArr = parseJsonbArray(rend.css)
    if (cssArr[0] && !map.has(0)) map.set(0, cssArr[0])
  }
  parseJsonbArray(pieza?.imagenes_urls).forEach((u, i) => {
    if (u && typeof u === 'string' && !map.has(i)) map.set(i, u)
  })
  const nSlides = parseJsonbArray(pieza?.slides).length
  const maxIdx = map.size ? Math.max(...map.keys()) : -1
  const n = Math.max(nSlides, maxIdx + 1, 1)
  return Array.from({ length: n }, (_, i) => map.get(i) || null)
}

// Caption + hashtags listos para pegar en IG
export function textoParaCopiar(pieza) {
  const tags = normalizarHashtags(pieza)
  return [(pieza?.caption || '').trim(), tags.join(' ')].filter(Boolean).join('\n\n')
}

export async function copiarAlPortapapeles(texto) {
  try {
    await navigator.clipboard.writeText(texto)
    return true
  } catch {
    try {
      const ta = document.createElement('textarea')
      ta.value = texto
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      return true
    } catch { return false }
  }
}

// ── Placeholder cuando el slide todavía no tiene imagen renderizada ──
// Muestra el contenido del slide sobre fondo navy para que igual "se lea" la pieza.
function SlidePlaceholder({ slide }) {
  if (!slide) {
    return (
      <div className="pigc-ph pigc-ph-vacio">
        <div className="pigc-ph-icono">🖼️</div>
        <div>Todavía sin imagen.<br />Se compone sola al publicar.</div>
      </div>
    )
  }
  return (
    <div className="pigc-ph">
      {(slide.sticker || slide.caption_top) && (
        <div className="pigc-ph-sticker">{stripNegritas(slide.sticker || slide.caption_top)}</div>
      )}
      {slide.titulo && <div className="pigc-ph-titulo">{stripNegritas(slide.titulo)}</div>}
      {slide.dato_principal && <div className="pigc-ph-dato">{stripNegritas(slide.dato_principal)}</div>}
      {slide.dato_sub && <div className="pigc-ph-sub">{stripNegritas(slide.dato_sub)}</div>}
      {slide.quote && <div className="pigc-ph-quote">"{stripNegritas(slide.quote)}"</div>}
      {slide.atribucion && <div className="pigc-ph-sub">— {stripNegritas(slide.atribucion)}</div>}
      {(slide.body || slide.cuerpo) && <div className="pigc-ph-body">{stripNegritas(slide.body || slide.cuerpo)}</div>}
      {slide.footer && <div className="pigc-ph-footer">{stripNegritas(slide.footer)}</div>}
    </div>
  )
}

// ── CelularIG · mockup del teléfono con la pieza como se ve en Instagram ──
export function CelularIG({ pieza }) {
  const [idx, setIdx] = useState(0)
  useEffect(() => { setIdx(0) }, [pieza?.id])

  if (!pieza) return null
  const slides = parseJsonbArray(pieza.slides)
  const imgs = imagenesDePieza(pieza)
  const n = Math.max(imgs.length, slides.length, 1)
  const i = Math.min(idx, n - 1)
  const esStory = FORMATOS_STORY.has(pieza.formato)
  const hashtags = normalizarHashtags(pieza)
  const img = imgs[i]
  const slide = slides[i] || null
  const sinImagen = !imgs.some(Boolean)
  const escena = escenaDePieza(pieza)
  const escenaGenerando = !img && escena?.status === 'generando'
  const escenaFallo = !img && escena?.status === 'error'

  return (
    <div className="pigc">
      <div className="pigc-bezel">
        <div className="pigc-notch" />
        <div className="pigc-screen">
          {/* Status bar */}
          <div className="pigc-sb"><span>9:41</span><span>📶 🔋</span></div>
          {/* Header IG */}
          <div className="pigc-igh">
            <div className="pigc-avatar">L</div>
            <div className="pigc-user">
              <strong>limpackuy</strong>
              <span>Montevideo, Uruguay</span>
            </div>
            <span className="pigc-dots">⋯</span>
          </div>
          {/* Imagen / carrusel */}
          <div className={`pigc-img ${esStory ? 'pigc-img-story' : ''}`}>
            {img ? (
              <img src={img} alt={`slide ${i + 1}`} />
            ) : escenaGenerando ? (
              <div className="pigc-ph pigc-ph-vacio">
                <div className="pigc-ph-icono">📸</div>
                <div>La IA está sacando la foto…<br />En un minuto aparece acá sola.</div>
              </div>
            ) : (
              <SlidePlaceholder slide={slide} />
            )}
            {n > 1 && <div className="pigc-counter">{i + 1}/{n}</div>}
            {n > 1 && i > 0 && (
              <button className="pigc-flecha pigc-flecha-izq" onClick={() => setIdx(i - 1)} aria-label="Slide anterior">‹</button>
            )}
            {n > 1 && i < n - 1 && (
              <button className="pigc-flecha pigc-flecha-der" onClick={() => setIdx(i + 1)} aria-label="Slide siguiente">›</button>
            )}
            {escenaGenerando && (
              <div className="pigc-badge-render pigc-badge-generando" title="generar-escena-post está creando la foto con Flux">
                📸 Generando la foto…
              </div>
            )}
            {escenaFallo && (
              <div className="pigc-badge-render pigc-badge-error" title={escena?.error || 'falló la generación'}>
                La foto falló — regenerala
              </div>
            )}
            {!img && !escenaGenerando && !escenaFallo && (
              <div className="pigc-badge-render" title="El Publisher compone la imagen final cuando llega el horario">
                La imagen se compone al publicar
              </div>
            )}
          </div>
          {/* Puntitos del carrusel */}
          {n > 1 && (
            <div className="pigc-nav">
              {Array.from({ length: n }).map((_, k) => (
                <button
                  key={k}
                  onClick={() => setIdx(k)}
                  className={`pigc-dot ${k === i ? 'active' : ''}`}
                  aria-label={`slide ${k + 1}`}
                />
              ))}
            </div>
          )}
          {/* Fila de acciones IG */}
          <div className="pigc-acts">
            <span>♡</span><span>💬</span><span>📤</span><span style={{ marginLeft: 'auto' }}>🔖</span>
          </div>
          {/* Caption COMPLETO + hashtags (lo que pidió Nico: foto, texto y #) */}
          <div className="pigc-cap">
            <strong>limpackuy</strong>{' '}
            <span className="pigc-cap-texto">{pieza.caption || '(sin caption todavía)'}</span>
            {hashtags.length > 0 && (
              <div className="pigc-cap-tags">{hashtags.join(' ')}</div>
            )}
            {sinImagen && (
              <div className="pigc-cap-nota">
                {escenaGenerando
                  ? 'La foto se está generando ahora — la vas a ver acá antes de aprobar'
                  : 'Preview sin render — la foto la genera el Publisher'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Bloques de detalle ──
function Dato({ label, children }) {
  if (children === null || children === undefined || children === '') return null
  return (
    <div className="pigm-dato">
      <span className="pigm-dato-l">{label}</span>
      <span className="pigm-dato-v">{children}</span>
    </div>
  )
}

const ORDEN_CAMPOS_SLIDE = [
  'template', 'sticker', 'caption_top', 'eyebrow', 'titulo', 'dato_principal',
  'dato_sub', 'dato_cap', 'quote', 'atribucion', 'body', 'cuerpo', 'cta',
  'footer', 'prompt_imagen', 'imagen_prompt',
]

function SlideDetalle({ slide, indice }) {
  const entradas = Object.entries(slide || {})
    .filter(([, v]) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0))
    .sort(([a], [b]) => {
      const ia = ORDEN_CAMPOS_SLIDE.indexOf(a); const ib = ORDEN_CAMPOS_SLIDE.indexOf(b)
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b)
    })
  return (
    <div className="pigm-slide">
      <div className="pigm-slide-num">Slide {indice + 1}</div>
      {entradas.length === 0 && <div className="pigm-vacio">(slide vacío)</div>}
      {entradas.map(([k, v]) => (
        <div key={k} className="pigm-slide-campo">
          <span className="pigm-slide-k">{k}</span>
          <span className="pigm-slide-v">{typeof v === 'string' ? v : JSON.stringify(v)}</span>
        </div>
      ))}
    </div>
  )
}

function fmtHorario(iso) {
  if (!iso) return null
  return new Date(iso).toLocaleString('es-UY', {
    weekday: 'long', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}

// ── PiezaModalIG · modal con el celular + TODO el detalle + acciones ──
// onAprobar() y onRechazar(motivo) son async; onAnterior/onSiguiente pueden ser null.
export function PiezaModalIG({ pieza, indice, total, onCerrar, onAnterior, onSiguiente, onAprobar, onRechazar }) {
  const [rechazando, setRechazando] = useState(false)
  const [motivo, setMotivo] = useState('')
  const [trabajando, setTrabajando] = useState(false)
  const [copiado, setCopiado] = useState(false)
  // Refresco en vivo de la imagen (la foto de escena se genera en background):
  // fresco pisa imagenes_renders/imagenes_urls de la pieza sin recargar el listado.
  const [fresco, setFresco] = useState(null)
  const [generandoFoto, setGenerandoFoto] = useState(false)
  // Edición a mano del caption (para no depender del agente por un cambio de palabras).
  const [editandoCap, setEditandoCap] = useState(false)
  const [capBorrador, setCapBorrador] = useState('')
  const [guardandoCap, setGuardandoCap] = useState(false)
  const [publicando, setPublicando] = useState(false)
  const [publicado, setPublicado] = useState(null)

  useEffect(() => {
    setRechazando(false); setMotivo(''); setCopiado(false); setFresco(null); setGenerandoFoto(false)
    setEditandoCap(false); setCapBorrador(''); setGuardandoCap(false)
    setPublicando(false); setPublicado(null)
  }, [pieza?.id])

  const pz = fresco && pieza && fresco.id === pieza.id ? { ...pieza, ...fresco } : pieza
  const escenaInfo = escenaDePieza(pz)

  // Mientras la foto se genera: poll cada 6s hasta que esté lista (o falle).
  useEffect(() => {
    if (!pieza?.id || escenaInfo?.status !== 'generando') return
    const t = setInterval(async () => {
      const { data } = await supabase.from('posts_ig')
        .select('id, imagenes_renders, imagenes_urls, engine_render')
        .eq('id', pieza.id).maybeSingle()
      if (data) setFresco(data)
    }, 6000)
    return () => clearInterval(t)
  }, [pieza?.id, escenaInfo?.status])

  async function generarFoto() {
    if (!pz?.id) return
    setGenerandoFoto(true)
    // Optimista: mostrar "generando" ya mismo (la function también lo marca en DB).
    const rendersPrev = parseJsonbObject(pz.imagenes_renders) || {}
    setFresco({ id: pz.id, imagenes_renders: { ...rendersPrev, escena: { ...(escenaInfo || {}), status: 'generando', at: new Date().toISOString() } } })
    try {
      const { error } = await supabase.functions.invoke('generar-escena-post', { body: { post_id: pz.id } })
      if (error) throw error
      const { data } = await supabase.from('posts_ig')
        .select('id, imagenes_renders, imagenes_urls, engine_render')
        .eq('id', pz.id).maybeSingle()
      if (data) setFresco(data)
    } catch (e) {
      alert('No pude generar la foto: ' + (e?.message || e) + '. Probá de nuevo en un rato.')
    } finally { setGenerandoFoto(false) }
  }

  // Publicar ESTA pieza, ahora, porque Nico apretó el botón (27-jul-2026).
  // Limpack no publica solo: el cron quedó desprogramado y esta es la única vía.
  // Doble confirmación a propósito — es irreversible y es la cuenta real.
  async function publicarAhora() {
    if (!pz?.id) return
    const fuente = escenaInfo?.status === 'lista' ? 'la FOTO' : 'la placa de texto (no hay foto lista)'
    if (!window.confirm(
      `Se va a publicar en Instagram AHORA, con ${fuente}.\n\n` +
      `"${(pz.caption || '').slice(0, 90)}…"\n\n` +
      `Esto no se puede deshacer desde acá: para bajarlo hay que borrarlo en Instagram. ¿Publicamos?`
    )) return
    setPublicando(true)
    try {
      const { data, error } = await supabase.functions.invoke('publicar-pieza-ahora', { body: { post_id: pz.id } })
      if (error) throw error
      if (data?.error) throw new Error(data.error)
      setPublicado({ permalink: data?.permalink, fuente: data?.imagen_fuente })
      if (data?.imagen_fuente && data.imagen_fuente !== 'foto_escena' && escenaInfo?.status === 'lista') {
        alert('OJO: se publicó pero con ' + data.imagen_fuente + ' en vez de la foto. Revisalo en Instagram.')
      }
    } catch (e) {
      alert('No se pudo publicar: ' + (e?.message || e))
    } finally { setPublicando(false) }
  }

  // Guardar el caption editado a mano. Queda auditado en metadata (editado_a_mano)
  // para que el Estratega no lo lea como si lo hubiera escrito el agente.
  async function guardarCaption() {
    if (!pz?.id) return
    const nuevo = capBorrador
    setGuardandoCap(true)
    try {
      const { error } = await supabase.from('posts_ig')
        .update({ caption: nuevo, caption_editado_a_mano: true })
        .eq('id', pz.id)
      if (error) throw error
      setFresco(f => ({ ...(f || {}), id: pz.id, caption: nuevo }))
      setEditandoCap(false)
    } catch (e) {
      alert('No pude guardar el caption: ' + (e?.message || e))
    } finally { setGuardandoCap(false) }
  }

  // Teclado: Esc cierra, flechas navegan (salvo escribiendo el motivo)
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') { rechazando ? setRechazando(false) : onCerrar() }
      if (e.target && (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT')) return
      if (e.key === 'ArrowLeft' && onAnterior) onAnterior()
      if (e.key === 'ArrowRight' && onSiguiente) onSiguiente()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [rechazando, onCerrar, onAnterior, onSiguiente])

  if (!pieza) return null

  const estado = ESTADO_PILL[pieza.estado] || { label: pieza.estado, cls: 'pig-pill-gris' }
  const hashtags = normalizarHashtags(pieza)
  const vald = validadorDePieza(pieza)
  const slides = parseJsonbArray(pieza.slides)
  const urls = parseJsonbArray(pz.imagenes_urls).filter(Boolean)
  const rendersObj = parseJsonbObject(pz.imagenes_renders)
  const redes = parseJsonbArray(pieza.redes)
  const briefDisenio = parseJsonbObject(pieza.brief_disenio) || (Array.isArray(pieza.brief_disenio) ? pieza.brief_disenio : null)
  const esFotoEscena = briefDisenio?.tipo_visual === 'foto_escena' || !!escenaInfo
  const trace = pieza.trace_dialogo
  const puedeDecidir = pieza.estado === 'generado' || pieza.estado === 'borrador'

  async function copiar() {
    const ok = await copiarAlPortapapeles(textoParaCopiar(pz))
    setCopiado(ok)
    if (ok) setTimeout(() => setCopiado(false), 2200)
    else alert('No pude copiar. Seleccioná el texto a mano.')
  }

  async function aprobar() {
    setTrabajando(true)
    try { await onAprobar() } finally { setTrabajando(false) }
  }

  async function confirmarRechazo() {
    setTrabajando(true)
    try {
      await onRechazar(motivo.trim() || '(sin motivo)')
      setRechazando(false)
      setMotivo('')
    } finally { setTrabajando(false) }
  }

  return (
    <div className="pigm-overlay" onClick={onCerrar}>
      <div className="pigm-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">

        {/* Header */}
        <div className="pigm-head">
          <div className="pigm-head-pills">
            <span className="pig-pill pig-pill-teal">{pieza.formato || 'feed'}</span>
            <span className="pig-pill pig-pill-gris">{PILAR_LABEL[pieza.pilar] || pieza.pilar || '—'}</span>
            <span className={`pig-pill ${estado.cls}`}>{estado.label}</span>
            {pieza.score_gerente != null && (
              <span className="pig-pill pig-pill-gold" title="Autoevaluación del Gerente">★ {pieza.score_gerente}/10</span>
            )}
          </div>
          <div className="pigm-head-nav">
            {total > 1 && (
              <>
                <button className="pigm-navbtn" onClick={onAnterior} disabled={!onAnterior} aria-label="Pieza anterior">‹</button>
                <span className="pigm-pos">{indice + 1} de {total}</span>
                <button className="pigm-navbtn" onClick={onSiguiente} disabled={!onSiguiente} aria-label="Pieza siguiente">›</button>
              </>
            )}
            <button className="pigm-cerrar" onClick={onCerrar} aria-label="Cerrar">×</button>
          </div>
        </div>

        {/* Cuerpo: celular + detalle */}
        <div className="pigm-body">
          <div className="pigm-col-celu">
            <CelularIG pieza={pz} />
            {esFotoEscena && (
              <button
                className="pigm-btn pigm-btn-ghost pigm-btn-copiar"
                onClick={generarFoto}
                disabled={generandoFoto || escenaInfo?.status === 'generando'}
              >
                {(generandoFoto || escenaInfo?.status === 'generando')
                  ? '📸 Generando la foto…'
                  : escenaInfo?.status === 'lista' ? '↻ Regenerar foto de escena' : '📸 Generar foto de escena'}
              </button>
            )}
            <button className="pigm-btn pigm-btn-ghost pigm-btn-copiar" onClick={copiar}>
              {copiado ? '✓ Copiado' : '📋 Copiar caption + #'}
            </button>

            {/* Publicación MANUAL: la única vía. El cron quedó apagado el 27-jul. */}
            {publicado ? (
              <div className="pigm-publicado">
                <div className="pigm-publicado-t">✓ Publicado en Instagram</div>
                <div className="pigm-publicado-s">
                  Subió {publicado.fuente === 'foto_escena' ? 'la foto' : publicado.fuente}
                </div>
                {publicado.permalink && (
                  <a href={publicado.permalink} target="_blank" rel="noreferrer" className="pigm-publicado-link">
                    Ver el post ↗
                  </a>
                )}
              </div>
            ) : !pz.ig_media_id ? (
              <>
                <button
                  className="pigm-btn pigm-btn-publicar"
                  onClick={publicarAhora}
                  disabled={publicando || escenaInfo?.status === 'generando'}
                >
                  {publicando ? 'Publicando…' : '🚀 Publicar ahora en Instagram'}
                </button>
                <div className="pigm-publicar-nota">
                  Nada sale solo. Sale cuando apretás este botón — y va {escenaInfo?.status === 'lista'
                    ? 'la foto que ves arriba'
                    : 'la placa de texto, porque todavía no hay foto lista'}.
                </div>
              </>
            ) : (
              <div className="pigm-publicar-nota">Esta pieza ya se publicó en Instagram.</div>
            )}
          </div>

          <div className="pigm-col-det">
            {/* ── Validador de hechos: la razón por la que podés aprobar sin chequear ── */}
            {vald.errores.length > 0 && (
              <div className="pigm-sec pigm-sec-val-err">
                <div className="pigm-sec-t">⛔ Datos falsos detectados ({vald.errores.length})</div>
                <ul className="pigm-val-lista">
                  {vald.errores.map((e, k) => <li key={k}>{e}</li>)}
                </ul>
                <div className="pigm-val-nota">El validador rebotó esta pieza contra la base de datos real — quedó como borrador, no sale así ni por error.</div>
              </div>
            )}
            {vald.errores.length === 0 && vald.advertencias.length > 0 && (
              <div className="pigm-sec pigm-sec-val-adv">
                <div className="pigm-sec-t">⚠️ Advertencias del validador ({vald.advertencias.length})</div>
                <ul className="pigm-val-lista">
                  {vald.advertencias.map((a, k) => <li key={k}>{a}</li>)}
                </ul>
              </div>
            )}
            {vald.verificada && (
              <div className="pigm-sec pigm-sec-val-ok">
                <div className="pigm-val-ok-t">✓ Datos verificados contra la base</div>
                <div className="pigm-val-ok-s">Barrios, días de ruta, precios, marcas y claims chequeados contra la base de datos real (no contra otra IA).</div>
              </div>
            )}

            {/* Feedback de rechazos previos — arriba de todo, es el loop de aprendizaje */}
            {vald.resto.length > 0 && (
              <div className="pigm-sec pigm-sec-feedback">
                <div className="pigm-sec-t">⚠️ Feedback previo ({vald.resto.length})</div>
                <ul className="pigm-feedback-lista">
                  {vald.resto.map((f, k) => <li key={k}>{f}</li>)}
                </ul>
              </div>
            )}

            <div className="pigm-sec">
              <div className="pigm-sec-t">📝 Brief</div>
              <div className="pigm-pre">{pieza.brief || '(sin brief)'}</div>
            </div>

            <div className="pigm-sec">
              <div className="pigm-sec-t">🗂️ Ficha de la pieza</div>
              <Dato label="Horario sugerido">{fmtHorario(pieza.horario_sugerido) || 'sin horario'}</Dato>
              <Dato label="Formato">{pieza.formato}{pieza.cantidad_slides ? ` · ${pieza.cantidad_slides} slides` : ''}</Dato>
              <Dato label="Pilar">{PILAR_LABEL[pieza.pilar] || pieza.pilar}</Dato>
              <Dato label="Estado">{estado.label}</Dato>
              <Dato label="Score del Gerente">{pieza.score_gerente != null ? `${pieza.score_gerente}/10` : null}</Dato>
              <Dato label="Redes">{redes.length ? redes.join(', ') : null}</Dato>
              <Dato label="Modelo">{pieza.modelo}</Dato>
              <Dato label="Engine de render">{pieza.engine_render}</Dato>
              <Dato label="Agente">{pieza.agente_owner}</Dato>
              <Dato label="Creada">{fmtHorario(pieza.created_at)}</Dato>
            </div>

            <div className="pigm-sec">
              <div className="pigm-sec-t">
                💬 Caption Instagram ({(pz.caption || '').length} chars)
                {!editandoCap && (
                  <button
                    className="pigm-inline-btn"
                    onClick={() => { setCapBorrador(pz.caption || ''); setEditandoCap(true) }}
                  >✏️ Editar a mano</button>
                )}
              </div>
              {editandoCap ? (
                <>
                  <textarea
                    className="pigm-cap-edit"
                    value={capBorrador}
                    onChange={e => setCapBorrador(e.target.value)}
                    rows={14}
                    autoFocus
                  />
                  <div className="pigm-cap-edit-acc">
                    <button className="pigm-btn pigm-btn-ok" onClick={guardarCaption} disabled={guardandoCap}>
                      {guardandoCap ? 'Guardando…' : '✓ Guardar caption'}
                    </button>
                    <button className="pigm-btn pigm-btn-ghost" onClick={() => setEditandoCap(false)} disabled={guardandoCap}>
                      Cancelar
                    </button>
                    <span className="pigm-cap-edit-nota">
                      Se guarda tal cual lo escribís — el agente no lo vuelve a tocar.
                    </span>
                  </div>
                </>
              ) : (
                <div className="pigm-pre">{pz.caption || '(sin caption)'}</div>
              )}
            </div>

            {pieza.caption_facebook && (
              <div className="pigm-sec">
                <div className="pigm-sec-t">📘 Caption Facebook</div>
                <div className="pigm-pre">{pieza.caption_facebook}</div>
              </div>
            )}

            <div className="pigm-sec">
              <div className="pigm-sec-t"># Hashtags ({hashtags.length})</div>
              {hashtags.length === 0 && <div className="pigm-vacio">(sin hashtags)</div>}
              <div className="pigm-tags">
                {hashtags.map((h, k) => <span key={k} className="pigm-tag">{h}</span>)}
              </div>
            </div>

            {slides.length > 0 && (
              <div className="pigm-sec">
                <div className="pigm-sec-t">🎠 Slides ({slides.length})</div>
                {slides.map((s, k) => <SlideDetalle key={k} slide={s} indice={k} />)}
              </div>
            )}

            <div className="pigm-sec">
              <div className="pigm-sec-t">🖼️ Imágenes</div>
              {urls.length === 0 && !rendersObj && (
                <div className="pigm-vacio">Todavía no hay imagen generada — el Publisher la compone al publicar.</div>
              )}
              {urls.map((u, k) => (
                <div key={k} className="pigm-url">
                  <a href={u} target="_blank" rel="noreferrer">imagen {k + 1} ↗</a>
                  <span className="pigm-url-txt">{u}</span>
                </div>
              ))}
              {rendersObj && (
                <details className="pigm-details">
                  <summary>Renders por slide (imagenes_renders)</summary>
                  <pre>{JSON.stringify(rendersObj, null, 2)}</pre>
                </details>
              )}
            </div>

            {briefDisenio && (
              <details className="pigm-details">
                <summary>🎨 Brief de diseño (brief_disenio)</summary>
                <pre>{JSON.stringify(briefDisenio, null, 2)}</pre>
              </details>
            )}

            {trace && (
              <details className="pigm-details">
                <summary>🧵 Diálogo del agente (trace_dialogo)</summary>
                <pre>{typeof trace === 'string' ? trace : JSON.stringify(trace, null, 2)}</pre>
              </details>
            )}
          </div>
        </div>

        {/* Acciones */}
        <div className="pigm-acts">
          {rechazando ? (
            <div className="pigm-rechazo">
              <label className="pigm-rechazo-l">
                ¿Por qué la rechazás? Queda guardado: el Gerente y el Estratega aprenden de esto.
              </label>
              <textarea
                className="pigm-rechazo-ta"
                value={motivo}
                onChange={e => setMotivo(e.target.value)}
                placeholder="Ej: el caption suena a jerga corporativa / la foto no muestra el producto / muy parecido al post del martes…"
                rows={3}
                autoFocus
              />
              <div className="pigm-rechazo-btns">
                <button className="pigm-btn pigm-btn-danger" onClick={confirmarRechazo} disabled={trabajando}>
                  {trabajando ? '…' : '✕ Confirmar rechazo'}
                </button>
                <button className="pigm-btn pigm-btn-ghost" onClick={() => setRechazando(false)} disabled={trabajando}>
                  Volver
                </button>
              </div>
            </div>
          ) : (
            <>
              {puedeDecidir && (
                <button className="pigm-btn pigm-btn-ok" onClick={aprobar} disabled={trabajando}>
                  {trabajando ? '…' : '✓ Aprobar'}
                </button>
              )}
              {puedeDecidir && (
                <button className="pigm-btn pigm-btn-danger" onClick={() => setRechazando(true)} disabled={trabajando}>
                  ✕ Rechazar
                </button>
              )}
              <button className="pigm-btn pigm-btn-ghost" onClick={copiar}>
                {copiado ? '✓ Copiado' : '📋 Copiar caption'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ── CSS del celular + modal (paleta oficial Navy/Teal/Bone/Gold) ──
export const PIEZA_PREVIEW_CSS = `
  /* — Pills propias (autocontenidas, no dependen de COM_CSS) — */
  .pig-pill {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 10px; font-weight: 800; letter-spacing: .04em;
    padding: 2px 8px; border-radius: 999px; text-transform: uppercase;
  }
  .pig-pill-teal { background: #E0F4F6; color: #00666B; }
  .pig-pill-gold { background: #FBF3E2; color: #8a6d2f; }
  .pig-pill-green { background: #D1FAE5; color: #065F46; }
  .pig-pill-gris { background: #F1F5F9; color: #475569; }
  .pig-pill-red { background: #FEE2E2; color: #991B1B; }

  /* — Chassis celular — */
  .pigc { display: flex; justify-content: center; }
  .pigc-bezel {
    background: #0F172A; border-radius: 34px; padding: 7px;
    position: relative; width: min(300px, 82vw);
    box-shadow: 0 10px 30px rgba(15, 31, 61, .22);
  }
  .pigc-notch {
    position: absolute; top: 11px; left: 50%; transform: translateX(-50%);
    width: 92px; height: 18px; background: #0F172A;
    border-radius: 0 0 12px 12px; z-index: 5;
  }
  .pigc-notch::after {
    content: ''; position: absolute; top: 5px; left: 50%;
    transform: translateX(-50%); width: 40px; height: 5px;
    background: #000; border-radius: 3px;
  }
  .pigc-screen {
    background: #fff; border-radius: 28px; overflow: hidden;
    display: flex; flex-direction: column;
    font-family: -apple-system, BlinkMacSystemFont, "Inter", system-ui, sans-serif;
  }
  .pigc-sb {
    display: flex; justify-content: space-between; align-items: center;
    padding: 12px 18px 4px; font-size: 11px; font-weight: 600; color: #0F172A;
  }
  .pigc-igh {
    display: flex; align-items: center; gap: 8px;
    padding: 6px 12px; border-bottom: 0.5px solid #F1F5F9;
  }
  .pigc-avatar {
    width: 28px; height: 28px; border-radius: 50%;
    background: linear-gradient(135deg, #1A3461, #00A8B5);
    color: #fff; display: flex; align-items: center; justify-content: center;
    font-weight: 700; font-size: 13px; flex-shrink: 0;
  }
  .pigc-user { flex: 1; display: flex; flex-direction: column; line-height: 1.2; min-width: 0; }
  .pigc-user strong { font-size: 12px; color: #0F172A; }
  .pigc-user span { font-size: 9.5px; color: #64748B; }
  .pigc-dots { color: #0F172A; font-size: 14px; }
  .pigc-img {
    position: relative; width: 100%; aspect-ratio: 4 / 5; background: #0F172A;
    overflow: hidden;
  }
  .pigc-img-story { aspect-ratio: 9 / 16; }
  .pigc-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .pigc-counter {
    position: absolute; top: 8px; right: 8px;
    background: rgba(15, 23, 42, .65); color: #fff; font-size: 10px;
    font-weight: 600; padding: 2px 7px; border-radius: 10px; z-index: 3;
  }
  .pigc-flecha {
    position: absolute; top: 50%; transform: translateY(-50%); z-index: 3;
    width: 26px; height: 26px; border-radius: 50%; border: 0; cursor: pointer;
    background: rgba(255,255,255,.88); color: #0F172A;
    font-size: 17px; line-height: 1; font-weight: 700;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 1px 4px rgba(0,0,0,.25); padding: 0 0 2px;
  }
  .pigc-flecha-izq { left: 8px; }
  .pigc-flecha-der { right: 8px; }
  .pigc-badge-render {
    position: absolute; left: 50%; bottom: 10px; transform: translateX(-50%);
    background: rgba(197,160,89,.95); color: #2b230f;
    font-size: 9px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase;
    padding: 3px 10px; border-radius: 999px; white-space: nowrap; z-index: 3;
  }
  .pigc-badge-generando {
    background: rgba(0,168,181,.95); color: #fff;
    animation: pigPulse 1.4s ease-in-out infinite;
  }
  .pigc-badge-error { background: #FEE2E2; color: #991B1B; }
  @keyframes pigPulse { 0%, 100% { opacity: 1; } 50% { opacity: .55; } }
  .pigc-nav { display: flex; gap: 4px; justify-content: center; padding: 7px 0 2px; background: #fff; }
  .pigc-dot { width: 6px; height: 6px; border-radius: 50%; background: #CBD5E1; border: 0; cursor: pointer; padding: 0; }
  .pigc-dot.active { background: #00A8B5; }
  .pigc-acts { display: flex; gap: 12px; padding: 7px 12px 4px; font-size: 18px; color: #0F172A; }
  .pigc-cap {
    padding: 2px 12px 14px; font-size: 11.5px; line-height: 1.45; color: #0F172A;
    max-height: 190px; overflow-y: auto; overscroll-behavior: contain;
  }
  .pigc-cap strong { font-weight: 700; }
  .pigc-cap-texto { white-space: pre-wrap; word-break: break-word; }
  .pigc-cap-tags { margin-top: 6px; color: #1E40AF; word-break: break-word; }
  .pigc-cap-nota { margin-top: 8px; font-size: 9.5px; color: #94A3B8; font-style: italic; }

  /* — Placeholder de slide sin render — */
  .pigc-ph {
    height: 100%; padding: 20px 16px; box-sizing: border-box;
    background: linear-gradient(135deg, #1A3461 0%, #0c1220 100%);
    color: #fff; display: flex; flex-direction: column; gap: 8px;
    justify-content: center; overflow: hidden;
  }
  .pigc-ph-vacio { align-items: center; text-align: center; color: #94A3B8; font-size: 11px; line-height: 1.5; }
  .pigc-ph-icono { font-size: 26px; }
  .pigc-ph-sticker {
    align-self: flex-start; font-size: 9px; font-weight: 800; letter-spacing: .04em;
    padding: 3px 9px; border-radius: 999px; background: rgba(255,255,255,.16); color: #fff;
  }
  .pigc-ph-titulo { font-size: 19px; font-weight: 800; line-height: 1.15; letter-spacing: -.02em; }
  .pigc-ph-dato { font-size: 26px; font-weight: 900; color: #00D4E0; letter-spacing: -.02em; }
  .pigc-ph-sub { font-size: 11px; color: rgba(255,255,255,.75); }
  .pigc-ph-quote { font-size: 14px; font-style: italic; line-height: 1.35; }
  .pigc-ph-body { font-size: 11.5px; line-height: 1.45; color: rgba(255,255,255,.85); }
  .pigc-ph-footer { font-size: 9.5px; color: #C5A059; font-weight: 700; margin-top: auto; }

  /* — Modal — */
  .pigm-overlay {
    position: fixed; inset: 0; z-index: 1200;
    background: rgba(10, 18, 35, .62); backdrop-filter: blur(2px);
    display: flex; align-items: center; justify-content: center; padding: 18px;
  }
  .pigm-modal {
    background: #F8F6F1; border-radius: 16px; width: min(980px, 100%);
    max-height: calc(100dvh - 36px); display: flex; flex-direction: column;
    overflow: hidden; box-shadow: 0 24px 60px rgba(10, 18, 35, .35);
  }
  .pigm-head {
    display: flex; align-items: center; justify-content: space-between; gap: 10px;
    padding: 12px 16px; background: #fff; border-bottom: 1px solid #E5E7EB; flex-wrap: wrap;
  }
  .pigm-head-pills { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
  .pigm-head-nav { display: flex; align-items: center; gap: 8px; margin-left: auto; }
  .pigm-pos { font-size: 11.5px; font-weight: 700; color: #6B7280; white-space: nowrap; }
  .pigm-navbtn {
    width: 30px; height: 30px; border-radius: 8px; border: 1px solid #E2E8F0;
    background: #F1F5F9; color: #1A3461; font-size: 17px; font-weight: 700;
    cursor: pointer; line-height: 1; padding: 0 0 2px;
  }
  .pigm-navbtn:disabled { opacity: .35; cursor: default; }
  .pigm-cerrar {
    width: 30px; height: 30px; border-radius: 8px; border: 0; background: transparent;
    color: #6B7280; font-size: 22px; cursor: pointer; line-height: 1; padding: 0 0 2px;
  }
  .pigm-cerrar:hover { color: #1A3461; }
  .pigm-body {
    display: grid; grid-template-columns: 340px 1fr; grid-template-rows: minmax(0, 1fr); gap: 0;
    overflow: hidden; flex: 1; min-height: 0;
  }
  .pigm-col-celu {
    padding: 18px 14px; display: flex; flex-direction: column; align-items: center; gap: 10px;
    overflow-y: auto; background: #F8F6F1; min-height: 0;
  }
  .pigm-btn-copiar { width: min(300px, 82vw); }
  .pigm-col-det {
    padding: 16px; overflow-y: auto; background: #fff; border-left: 1px solid #EDF0F4;
    display: flex; flex-direction: column; gap: 12px; min-width: 0; min-height: 0;
  }
  .pigm-sec { border: 1px solid #EDF0F4; border-radius: 10px; padding: 10px 12px; }
  .pigm-sec-t {
    font-size: 10.5px; font-weight: 800; color: #1A3461; letter-spacing: .06em;
    text-transform: uppercase; margin-bottom: 7px;
    display: flex; align-items: center; justify-content: space-between; gap: 8px;
  }

  /* — Editar el caption a mano (sin gastar una corrida del agente) — */
  .pigm-inline-btn {
    border: 1px solid #CBD5E1; background: #fff; color: #1A3461;
    font-size: 10.5px; font-weight: 700; letter-spacing: .02em; text-transform: none;
    padding: 3px 9px; border-radius: 999px; cursor: pointer; flex-shrink: 0;
  }
  .pigm-inline-btn:hover { background: #F1F5F9; border-color: #1A3461; }
  .pigm-cap-edit {
    width: 100%; box-sizing: border-box; resize: vertical;
    font-family: inherit; font-size: 12.5px; line-height: 1.5; color: #111827;
    border: 1px solid #1A3461; border-radius: 8px; padding: 9px 10px; background: #fff;
  }
  .pigm-cap-edit:focus { outline: 2px solid #2AA9A0; outline-offset: 1px; }
  .pigm-cap-edit-acc {
    display: flex; align-items: center; gap: 8px; margin-top: 8px; flex-wrap: wrap;
  }
  .pigm-cap-edit-nota { font-size: 11px; color: #6B7280; }

  /* — Publicar a mano (única vía desde el 27-jul: el cron está apagado) — */
  .pigm-btn-publicar {
    background: #1A3461; color: #fff; width: 100%; margin-top: 8px;
    font-weight: 700; padding: 11px 14px;
  }
  .pigm-btn-publicar:hover:not(:disabled) { background: #16294E; }
  .pigm-btn-publicar:disabled { opacity: .55; cursor: not-allowed; }
  .pigm-publicar-nota {
    font-size: 11px; color: #6B7280; line-height: 1.45; margin-top: 6px; text-align: center;
  }
  .pigm-publicado {
    margin-top: 8px; padding: 10px 12px; border-radius: 10px;
    background: #ECFDF5; border: 1px solid #A7F3D0; text-align: center;
  }
  .pigm-publicado-t { font-size: 13px; font-weight: 800; color: #065F46; }
  .pigm-publicado-s { font-size: 11.5px; color: #047857; margin-top: 2px; }
  .pigm-publicado-link {
    display: inline-block; margin-top: 6px; font-size: 12px; font-weight: 700; color: #065F46;
  }
  .pigm-sec-feedback { background: #FFF7ED; border-color: #FED7AA; }

  /* — Validador de hechos — */
  .pigm-sec-val-ok { background: #ECFDF5; border-color: #A7F3D0; }
  .pigm-val-ok-t { font-size: 13px; font-weight: 800; color: #065F46; }
  .pigm-val-ok-s { font-size: 11.5px; line-height: 1.45; color: #047857; margin-top: 3px; }
  .pigm-sec-val-adv { background: #FFFBEB; border-color: #FDE68A; }
  .pigm-sec-val-adv .pigm-sec-t { color: #92400E; }
  .pigm-sec-val-adv .pigm-val-lista { color: #92400E; }
  .pigm-sec-val-err { background: #FEF2F2; border-color: #FECACA; }
  .pigm-sec-val-err .pigm-sec-t { color: #991B1B; }
  .pigm-sec-val-err .pigm-val-lista { color: #991B1B; }
  .pigm-val-lista { margin: 0; padding-left: 18px; font-size: 12px; line-height: 1.45; display: flex; flex-direction: column; gap: 4px; }
  .pigm-val-nota { margin-top: 7px; font-size: 11px; font-weight: 700; color: #991B1B; }
  .pigm-feedback-lista { margin: 0; padding-left: 18px; font-size: 12px; color: #7C2D12; display: flex; flex-direction: column; gap: 4px; }
  .pigm-pre { font-size: 12.5px; line-height: 1.5; color: #111827; white-space: pre-wrap; word-break: break-word; }
  .pigm-vacio { font-size: 12px; color: #9CA3AF; font-style: italic; }
  .pigm-dato { display: flex; gap: 10px; padding: 3px 0; font-size: 12px; align-items: baseline; }
  .pigm-dato-l { min-width: 118px; color: #6B7280; font-weight: 600; flex-shrink: 0; }
  .pigm-dato-v { color: #111827; word-break: break-word; }
  .pigm-tags { display: flex; flex-wrap: wrap; gap: 4px; }
  .pigm-tag {
    font-size: 10.5px; font-weight: 600; color: #00666B; background: #E0F4F6;
    border-radius: 999px; padding: 2px 8px; white-space: nowrap;
  }
  .pigm-slide { border: 1px dashed #E2E8F0; border-radius: 8px; padding: 8px 10px; margin-bottom: 8px; }
  .pigm-slide:last-child { margin-bottom: 0; }
  .pigm-slide-num { font-size: 10px; font-weight: 800; color: #00A8B5; text-transform: uppercase; letter-spacing: .06em; margin-bottom: 4px; }
  .pigm-slide-campo { display: flex; gap: 8px; font-size: 11.5px; padding: 2px 0; align-items: baseline; }
  .pigm-slide-k { min-width: 96px; color: #94A3B8; font-family: ui-monospace, monospace; font-size: 10.5px; flex-shrink: 0; }
  .pigm-slide-v { color: #111827; white-space: pre-wrap; word-break: break-word; }
  .pigm-url { font-size: 11.5px; padding: 3px 0; display: flex; flex-direction: column; gap: 1px; }
  .pigm-url a { color: #00A8B5; font-weight: 700; text-decoration: none; }
  .pigm-url a:hover { text-decoration: underline; }
  .pigm-url-txt { color: #9CA3AF; font-size: 10px; word-break: break-all; }
  .pigm-details { border: 1px solid #EDF0F4; border-radius: 10px; padding: 8px 12px; font-size: 12px; }
  .pigm-details summary { cursor: pointer; font-weight: 700; color: #1A3461; font-size: 12px; }
  .pigm-details pre {
    margin: 8px 0 2px; background: #0F172A; color: #E2E8F0; border-radius: 8px;
    padding: 10px; font-size: 10.5px; overflow: auto; max-height: 320px;
  }

  .pigm-acts {
    display: flex; gap: 8px; padding: 12px 16px; background: #fff;
    border-top: 1px solid #E5E7EB; flex-wrap: wrap;
  }
  .pigm-btn {
    border: 0; border-radius: 8px; padding: 10px 16px; font-size: 13px; font-weight: 700;
    cursor: pointer; font-family: inherit; transition: opacity .12s;
  }
  .pigm-btn:disabled { opacity: .55; cursor: default; }
  .pigm-btn-ok { background: #10B981; color: #fff; flex: 1; min-width: 130px; }
  .pigm-btn-danger { background: #fff; color: #DC2626; border: 1px solid #FCA5A5; }
  .pigm-btn-ghost { background: #F1F5F9; color: #1A3461; border: 1px solid #E2E8F0; }
  .pigm-rechazo { display: flex; flex-direction: column; gap: 8px; width: 100%; }
  .pigm-rechazo-l { font-size: 12px; font-weight: 600; color: #7C2D12; }
  .pigm-rechazo-ta {
    width: 100%; box-sizing: border-box; padding: 9px 11px; font-size: 13px; line-height: 1.5;
    border: 1px solid #FCA5A5; border-radius: 8px; font-family: inherit; resize: vertical;
    background: #FFF7F7;
  }
  .pigm-rechazo-btns { display: flex; gap: 8px; }

  /* — Mobile (el panel se usa desde el celular, ≤414px incluido) — */
  @media (max-width: 800px) {
    .pigm-overlay { padding: 0; align-items: stretch; }
    .pigm-modal { border-radius: 0; width: 100%; max-height: 100dvh; height: 100dvh; }
    .pigm-body { grid-template-columns: 1fr; overflow-y: auto; display: flex; flex-direction: column; }
    .pigm-col-celu { overflow: visible; padding: 16px 12px 6px; }
    .pigm-col-det { overflow: visible; border-left: 0; border-top: 1px solid #EDF0F4; }
    .pigm-head { position: sticky; top: 0; z-index: 2; }
    .pigm-acts { position: sticky; bottom: 0; z-index: 2; box-shadow: 0 -4px 12px rgba(10,18,35,.06); }
  }
`
